import React, { createContext, useContext, useState, useEffect } from 'react';

const BACKEND_URL = window.location.origin;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Fetch favorites after successful auth
        fetchFavorites();
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setFavorites([]);
    }
  };

  const processSessionId = async (sessionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        fetchFavorites();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session processing failed:', error);
      return false;
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/favorites`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    }
  };

  const addToFavorites = async (courseId) => {
    if (!user) {
      login();
      return false;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/favorites/${courseId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        setFavorites(prev => [...prev, courseId]);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to add favorite:', error);
      return false;
    }
  };

  const removeFromFavorites = async (courseId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/favorites/${courseId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setFavorites(prev => prev.filter(id => id !== courseId));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to remove favorite:', error);
      return false;
    }
  };

  const isFavorite = (courseId) => {
    return favorites.includes(courseId);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      processSessionId,
      favorites,
      addToFavorites,
      removeFromFavorites,
      isFavorite,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
